import { Injectable } from '@angular/core';
import { BlockQuery } from '../../../tclasses/queries/block.query';
import { EventQuery } from '../../../tclasses/queries/event.query';
import {
    BlockLossesQuery,
    ProductionLossesActualProductionQuery,
    ProductionLossesNeighbouringProductionQuery,
    ProductionLossesManufacturerTheoreticalProductionQuery,
    ProductionLossesHistoricalTheoreticalProductionQuery,
    TurbineDataQuery,
    ProductionLossesWaterfallQuery
} from '../../../tclasses/queries/block-losses.query';
import { SaveBlockQuery } from '../../../tclasses/queries/save-block.query';
import { catchError, map } from 'rxjs/operators';
import { BlockInterface } from '../../../tclasses/interface/block.interface';
import { Observable, throwError } from 'rxjs';
import { ParkLogbookLossesQuery, ParkTclassBlockQuery } from '../../../tclasses/queries/park-logbook.query';
import { EventFilterQuery } from '../../../tclasses/queries/event-filter.query';
import { TranslateService } from '@ngx-translate/core';
import { BlocksHelper } from '../../helper/blocks.helper';
import { BlockComponentQuery } from '../../../tclasses/queries/block-component.query';
import { BlockServiceHelper } from '../../../tclasses/helpers/blocks-service.helper';
import { ForecastProvidersEnum } from '../data-services/graphql';
import { TimezoneService } from '../timezone/timezone.service';

@Injectable({
    providedIn: 'root'
})
export class BlockService {

    calculationMethodList = BlockServiceHelper.calculationMethodList;

    public startTS: number;
    public endTS: number;

    constructor(
        public blocksHelper: BlocksHelper,
        private blockQuery: BlockQuery,
        private eventQuery: EventQuery,
        private eventFilterQuery: EventFilterQuery,
        private blockLossesQuery: BlockLossesQuery,
        private parkTclassBlockQuery: ParkTclassBlockQuery,
        private blockComponentQuery: BlockComponentQuery,
        private productionLossesActualProduction: ProductionLossesActualProductionQuery,
        private productionLossesManufacturerTheoreticalProduction: ProductionLossesManufacturerTheoreticalProductionQuery,
        private productionLossesHistoricalTheoreticalProduction: ProductionLossesHistoricalTheoreticalProductionQuery,
        private productionLossesNeighbouringProduction: ProductionLossesNeighbouringProductionQuery,
        private productionLossesWaterfall: ProductionLossesWaterfallQuery,
        private turbineDataQuery: TurbineDataQuery,
        private parkLogbookLossesQuery: ParkLogbookLossesQuery,
        private saveBlockQuery: SaveBlockQuery,
        private translateService: TranslateService,
        private timezoneService: TimezoneService
    ) {}

    getBlocks(serialNav: number, from: number, to: number, monthStartTS: number, monthEndTS: number, capped = false,timezone: string = null) {
      this.startTS = from;
      this.endTS = to;
      let timeZone = timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
      return this.blockQuery
        .fetch(
          {
            variables: {
              serialNav, 
              fromTS: this.timezoneService.applyTzToDate(from).valueOf(), 
              toTS: this.timezoneService.applyTzToDate(to).valueOf(), 
              monthStartTS: this.timezoneService.applyTzToDate(monthStartTS).valueOf(),
              monthEndTS: this.timezoneService.applyTzToDate(monthEndTS).valueOf(),
              capped, 
              timeZone
            },
            fetchPolicy: 'no-cache' 
          }
        )
        .pipe(
            map((r) => {
                const response = JSON.parse(JSON.stringify(r));
                return BlockServiceHelper.mapLobookBlockResponse(response, this.timezoneService);
            })
        );
    }

    getBlockEvents(serialNav: number, from: number, to: number, capped = true, pageSize: number = null, pageNumber: number = null, lastPage: boolean = false, eventCodeFilter: string = null, eventCodeExcludeFilter: string = null, eventCodeExceptionFilter: string[] = null) {
      return this.eventQuery
        .fetch({
            variables: {
              serialNav, 
              fromTS: this.timezoneService.applyTzToDate(from).valueOf(), 
              toTS: this.timezoneService.applyTzToDate(to).valueOf(), 
              capped, 
              pageSize, 
              pageNumber, 
              lastPage, 
              eventCodeFilter, 
              eventCodeExcludeFilter, 
              eventCodeExceptionFilter
          },
          fetchPolicy: 'network-only' 
        })
        .pipe(
            map((response: any) => {
              const responseEvents = JSON.parse(JSON.stringify(response.data.turbines[0].turbineLogbook.turbineLogbookEvents.events));
              responseEvents.forEach(e => {
                e.createTS = this.timezoneService.unApplyTzFromDate(e.createTS).valueOf();
              })
              return ({
                events: responseEvents,
                eventCount: response.data.turbines[0].turbineLogbook.turbineLogbookEvents.totalCount
              });
            })
        )
        .toPromise();
    }

    /* Get block losses within the waterfall losses method */
    getWaterfallBlockLosses(turbine: any, from: any, to: any) {
      return this.productionLossesWaterfall
        .fetch({ 
          variables: { serialNav: +turbine, fromTS: from, toTS: to  },
          fetchPolicy: 'network-only' 
        })
        .pipe(
          map((b) => b.data.turbines[0] && b.data.turbines[0].customWaterfallProductionLoss ? b.data.turbines[0].customWaterfallProductionLoss : null)
        )
        .toPromise();
      }

    /* Get 2 block losses. Used when merging events from one block into another or when splitting blocks */
    getBlockLosses(turbine: any, actualBlockFrom: any, actualBlockTo: any, newBlockfrom: any, newBlockTo: any, actualBlockMethod: any, actualBlockSubMethod: any, actualBlockSelection: any, nextBlockMethod: any, nextBlockSubMethod: any, nextBlockSelection: any) {
      return Promise.all([
        this.blockLossesQuery
          .fetch({
            variables: {
              serialNav: +turbine, fromTS: actualBlockFrom, toTS: actualBlockTo, method: actualBlockMethod, 
              submethod: actualBlockSubMethod, selection: actualBlockSelection
            },
            fetchPolicy: 'network-only'
          })
          .pipe(
              map((b) => b.data.turbines[0] && b.data.turbines[0].lossValues ? b.data.turbines[0].lossValues : null)
          )
          .toPromise(),
        this.blockLossesQuery
          .fetch({ 
            variables: {
              serialNav: +turbine, fromTS: newBlockfrom, toTS: newBlockTo, method: nextBlockMethod, 
              submethod: nextBlockSubMethod, selection: nextBlockSelection
            },
            fetchPolicy: 'network-only'
          })
          .pipe(
              map((b) => b.data.turbines[0] && b.data.turbines[0].lossValues ? b.data.turbines[0].lossValues : null)
          )
          .toPromise()
      ]);
    }

    /* Get losses values in the prod. losses popup. The method indicates the way that
    losses are calculated. Method code and meaning are specified in the query file  */
    getSingleBlockLosses( turbine: any, from: any, to: any, method: any, submethod: any, selection: any, productionLossInput?: any, revenueLossInput?: any, provider?: ForecastProvidersEnum) {
      return this.blockLossesQuery
        .fetch({
          variables: {
            serialNav: +turbine, fromTS: from, toTS: to, method, submethod, selection, productionLossInput, revenueLossInput, provider
          },
          fetchPolicy: 'network-only'
        })
        .pipe(
          map((b) => b.data.turbines[0] && b.data.turbines[0].lossValues ? b.data.turbines[0].lossValues : null)
        )
        .toPromise();
    }

    /* Get actual production losses values */
    getActualPopupLosses(turbine: number, from: number, to: number) {
      return this.productionLossesActualProduction
        .fetch({ 
            variables: {
              serialNav: +turbine, fromTS: from, toTS: to
            },
            fetchPolicy: 'network-only'
          }
        )
        .pipe(map((r) => r.data.turbines[0]))
        .toPromise();
    }

    /* Get theoretical production losses values */
    getTheoreticalManufacturerPopupLosses(turbine: number, from: number, to: number) {
      return this.productionLossesManufacturerTheoreticalProduction
        .fetch({ 
            variables: {
              serialNav: +turbine, fromTS: from, toTS: to
            },
            fetchPolicy: 'network-only'
          })
        .pipe(map((r) => r.data.turbines[0]))
        .toPromise();
    }

    getTheoreticalHistoricalPopupLosses(turbine: number, from: number, to: number) {
      return this.productionLossesHistoricalTheoreticalProduction
        .fetch({ 
          variables: { serialNav: +turbine, fromTS: from, toTS: to }, 
          fetchPolicy: 'network-only' 
        })
        .pipe(map((r) => r.data.turbines[0]))
        .toPromise();
    }

    /* Get neighbour parks losses values */
    getNeighbourPopupLosses(turbine: number, from: number, to: number) {
      return this.productionLossesNeighbouringProduction
        .fetch({ 
          variables: { serialNav: +turbine, fromTS: from, toTS: to }, 
          fetchPolicy: 'network-only' 
        })
        .pipe(map((r) => r.data.turbines[0]))
        .toPromise();
    }

    getTurbineData(turbine: number) {
      return this.turbineDataQuery
        .fetch({ 
          variables: { serialNav: +turbine  },
          fetchPolicy: 'network-only' 
        })
        .pipe(map((r) => r.data.turbines[0]))
        .toPromise();
    }

    getBlockByEventCode(turbine: number, from: number, to: number, code: any) {
      return this.eventFilterQuery
        .fetch({ 
          variables: { serialNav: +turbine, fromTS: from, toTS: to, code },
          fetchPolicy: 'network-only' 
        })
        .pipe(map((r) => r.data.turbines[0].blockEventCodeFilter))
        .toPromise();
    }

    getBlockComponentByEventCode(turbine: number, code: any) {
      return this.blockComponentQuery
        .fetch({
          variables: { serialNav: +turbine, code },
          fetchPolicy: 'network-only'
        })
        .pipe(map((r) => r.data.turbines[0].logbookComponentsByAlarmCode))
        .toPromise();
    }

    getParkLogbook(park: string, from: number, to: number, turbineIds = null) {
      this.startTS = from;
      this.endTS = to;
      return this.parkLogbookLossesQuery
        .fetch({
          variables: { name: park, startTS: from, endTS: to, turbineIds },
          fetchPolicy: 'network-only'
        })
        .pipe(
          map((response: any) => BlockServiceHelper.mapParkLogbookResponse(JSON.parse(JSON.stringify(response))))
        )
        .toPromise();
    }

    getParkTclassBlocks(park: string, from: number, to: number, turbineIds) {
      return this.parkTclassBlockQuery
        .fetch({
          variables: { park, startTS: from, endTS: to, turbineIds },
          fetchPolicy: 'network-only'
        })
        .pipe(
          map((response: any) => BlockServiceHelper.mapTclassBlocksResponse(JSON.parse(JSON.stringify(response))))
        )
      .toPromise();
    }

    saveConfirmedBlock(block: BlockInterface, originalBlocks: any) {
      const savedBlock = BlockServiceHelper.prepareBlockForSaving(block);

      const originalBlocksToSave = [];
      originalBlocks.forEach((b) => {
        const bl = BlockServiceHelper.prepareBlockForSaving(b);
        originalBlocksToSave.push(bl);
      });

      return this.saveBlockQuery
        .mutate({variables: { level: 'park', input: savedBlock, inputEvent: [], originalBlocks: originalBlocksToSave,
                  startTS: this.startTS, endTS: this.endTS }})
        .pipe(
          catchError((error) => {
            let errorText = '';
            if (
              [ 'ERROR_CODE_CLICKHOUSE_2', 'ERROR_CODE_CLICKHOUSE_3', 'ERROR_CODE_MONGO_1', 'ERROR_CODE_MONGO_2' ].indexOf(
                  error.graphQLErrors?.[0]?.extensions?.code
              ) !== -1
            ) {
              errorText = this.translateService.instant('LOGBOOK.ERRORS.GENERIC_SAVE_ERROR');
            } else {
              if (error.graphQLErrors?.[0]?.extensions?.code === 'ERROR_CODE_SAVE_COMPROBATION') {
                errorText = this.translateService.instant('LOGBOOK.ERRORS.NOT_LAST_BLOCK_VERSION_SAVE');
              } else {
                errorText = this.translateService.instant('LOGBOOK.ERRORS.UNKNOWN_SAVE_ERROR');
              }
            }
            return throwError(errorText);
          }),
          map((params: any) => params.data.saveTurbineLogbookEntry)
        )
        .toPromise();
    }

    saveMultipleConfirmedBlocks(blocks: BlockInterface[], originalBlocks: any) {
      const savedBlocks = [];
      blocks.forEach((b) => {
        const savedBlock = BlockServiceHelper.prepareBlockForSaving(b);
        savedBlocks.push(savedBlock);
      });

      const originalBlocksToSave = [];
      originalBlocks.forEach((b) => {
        const block = BlockServiceHelper.prepareBlockForSaving(b);
        originalBlocksToSave.push(block);
      });

      return this.saveBlockQuery
        .mutate({variables: { level: 'park', input: savedBlocks, inputEvent: [], originalBlocks: originalBlocksToSave, 
                  startTS: this.startTS, endTS: this.endTS }})
        .pipe(
          catchError((error) => {
            let errorText = '';
            if (
              [ 'ERROR_CODE_CLICKHOUSE_3', 'ERROR_CODE_MONGO_1', 'ERROR_CODE_MONGO_2' ].indexOf(
                  error.graphQLErrors?.[0]?.extensions?.code
              ) !== -1
            ) {
              errorText = this.translateService.instant('LOGBOOK.ERRORS.GENERIC_SAVE_ERROR');
            } else {
              if (error.graphQLErrors?.[0]?.extensions?.code === 'ERROR_CODE_SAVE_COMPROBATION') {
                errorText = this.translateService.instant('LOGBOOK.ERRORS.NOT_LAST_BLOCK_VERSION_SAVE');
              } else {
                errorText = this.translateService.instant('LOGBOOK.ERRORS.UNKNOWN_SAVE_ERROR');
              }
            }
            return throwError(errorText);
          }),
          map((params: any) => params.data.saveTurbineLogbookEntry)
        )
        .toPromise();
    }

    getBlockColors() {
      return this.blocksHelper.blockColors;
    }

    getClassName(block: any) {
      return this.blocksHelper.tclasses.filter((b) => b.id === block.tcu)[0].description;
    }

    getGenericClass(cls: any) {
      return cls ? cls.split('.')[0] : '';
    }

    showAckWarning(block) {
      return ![ 'T0', 'T5', 'T6' ].includes(this.getGenericClass(block.tcu));
    }

    getMaintainColor(type: any) {
      return this.blocksHelper.maintainColors.filter((b) => b.type === type)[0]
        ? this.blocksHelper.maintainColors.filter((b) => b.type === type)[0].color
        : '';
    }

    getSpecificColor(type: any, cls: any) {
      return cls ? this.blocksHelper.blockColors.filter((b) => b.class === cls.split('.')[0])[0].lineColor : '';
    }

    getSpecificVarColor(type: any, cls: any) {
      return cls ? this.blocksHelper.blockColors.filter((b) => b.class === cls.split('.')[0])[0].cssVarColor : '';
    }

    getTClassColor(cls: string) {
      return cls.split('.')[0];
    }

    getLossesMethodDescription(value) {
      return value ? this.calculationMethodList.filter((m) => m.id === value)[0].description : '';
    }
}
